/**
 * DeleteFinancialAccountModal Component
 * 
 * Modal for confirming financial account deletion.
 * 
 * Features:
 * - Modal overlay with focus trap
 * - Case-sensitive DELETE confirmation
 * - Keyboard navigation support
 * - WCAG 2.2 AA compliance
 */

import React, { useEffect, useRef, useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';

interface DeleteFinancialAccountModalProps {
  isOpen: boolean;
  accountName: string;
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
}

const REQUIRED_CONFIRMATION = 'DELETE';

export function DeleteFinancialAccountModal({ 
  isOpen, 
  accountName,
  onClose, 
  onConfirm,
  loading = false
}: DeleteFinancialAccountModalProps): React.ReactElement | null {
  const modalRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLButtonElement>(null);
  const confirmationInputRef = useRef<HTMLInputElement>(null);
  const [confirmationText, setConfirmationText] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setConfirmationText('');
      setErrors({});
    }
  }, [isOpen]);

  // Focus trap and keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    
    // Focus the confirmation input
    if (confirmationInputRef.current) {
      confirmationInputRef.current.focus();
    }

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmationText(e.target.value);
    // Clear error
    if (errors.confirmation) {
      setErrors(prev => {
        const next = { ...prev };
        delete next.confirmation;
        return next;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (confirmationText !== REQUIRED_CONFIRMATION) {
      newErrors.confirmation = `Please type "${REQUIRED_CONFIRMATION}" to confirm`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    onConfirm();
  };

  const isConfirmButtonDisabled = loading || confirmationText !== REQUIRED_CONFIRMATION;

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-account-title"
      aria-describedby="delete-account-description"
    >
      <div
        ref={modalRef}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
            <h2 id="delete-account-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Delete Account
            </h2>
          </div>
          <button
            ref={firstFocusableRef}
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label="Close modal"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="px-6 py-4">
          <div className="space-y-4">
            <p id="delete-account-description" className="text-sm text-gray-600 dark:text-gray-400">
              This will permanently delete <strong className="text-gray-900 dark:text-gray-100">{accountName}</strong> and all associated data including:
            </p>

            {/* Warning Message */}
            <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-4">
              <ul className="list-disc list-inside text-sm text-red-800 dark:text-red-200 space-y-1">
                <li>All transactions for this account</li>
                <li>All account balances and history</li>
                <li>All account settings and preferences</li>
              </ul>
              <p className="mt-2 text-sm font-medium text-red-800 dark:text-red-200">
                This action cannot be undone.
              </p>
            </div>

            {/* Confirmation Input */}
            <div>
              <label 
                htmlFor="delete-confirmation"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Type <strong>DELETE</strong> to confirm:
              </label>
              <input
                ref={confirmationInputRef}
                id="delete-confirmation"
                type="text"
                value={confirmationText}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                  errors.confirmation
                    ? 'border-red-300 dark:border-red-700'
                    : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder="DELETE"
                disabled={loading}
                aria-invalid={!!errors.confirmation}
                aria-describedby={errors.confirmation ? 'confirmation-error' : undefined}
              />
              {errors.confirmation && (
                <p id="confirmation-error" className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {errors.confirmation}
                </p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isConfirmButtonDisabled}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors"
              >
                {loading ? 'Deleting...' : 'Delete Account'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

